// THE ANGLER, "the Fifth Cowboy": a rival gunslinger fish-mech built from laundry-room junk
// (docs/FISH_BOSS_DESIGN.md, Godot scripts/enemies/fish_robot.gd + scripts/bosses/angler_*.gd,
// docs/BOSS_GRAMMAR.md §5 row "weak-point AP menu | element puzzle | shield gates").
// Server-only brain; the body is ONE ordinary enemy (EType.Angler) so grenades, rockets, melee,
// specials, prime/detonate, burn and lag compensation all reach it. Its piranhas are Swarmers.
//
// Read surface: the weak-point AP menu. Core verb: the element puzzle. Windows: shield gates.
//   The chassis is AP5 everywhere (clang). The gold paths:
//     EYES     two small gold eyes, AV1 ×1.25, ALWAYS open (the any-kit path, G7)
//     BELLY    the drum behind the chest porthole, AV0 ×1.5, open only in a kick / stomp recovery and in
//              every stagger (punish the recovery: the Phase-1 lesson)
//     LURE     the bulb on the rod, AV1 gold; during a gate it is the crack-meter router
//     PANELS   seven armour panels a blast knocks off (any explosion that bites, or a detonation),
//              leaving AV3 plating under them: ordnance carves its own kill path
//   WET is the spine: it self-douses (and Phase 3 is permanently wet). A shock proc on the wet
//   Angler electrocutes it (45, armour-bypassing) and every piranha within 9 m; an ice proc
//   flash-freezes it (2 s, +25 % damage); fire hisses straight off. A cowboy who is wet himself
//   eats a 14 backfire when his shock lands: find a dry perch first.
//   Gates at 66 % and 33 %: a magenta hyper shield (10 % damage) that only a prime → detonate
//   breaks, or the crack meter fed through the eyes / lure. HP floors at the gate: no skipping.
//
// The rising water (the arena-as-clock) is a FLOOD the Angler pumps out of its tank: a shallow
// pool round the arena centre whose radius is the phase clock (a puddle, then 16 m, then 28 m, then
// the death surge). Standing in it makes you WET; the rock pillars and shelves are the dry perches.
//
// One promise at a time (G1). Every attack is TELL → EXECUTE → RECOVER (≥ 0.9 s) → a movement beat.
//   P1 THE DUEL      PALM SHOT (2 cyan bolts), KICK, STOMP (ring: jump it)
//   P2 HIGH TIDE     + SELF-DOUSE, PRESSURE HOSE (shove cone), GEYSER FIELD (locked strike rings:
//                    launch + wet), PIRANHA CALL (cap 3, 10 s lockout per kill), BOMB HURL (lobs)
//   P3 MAELSTROM     + WATER BARRIER (the red melee lock: someone runs in and hits it), MAELSTROM
//                    (the super: a 1.6 s leap, then the whole flood slams; get out of the water)

import { DT, clamp, len2, secs, wrapAngle } from '../core';
import type { Reader, Writer } from '../codec';
import { Ev } from '../codec';
import { ENEMIES, EType } from '../defs';
import { BKind, BULLETS, lobVelocity } from '../bullets';
import { EState, Enemy, historyAt, stepStatus } from '../enemies';
import { PlayerState, isTargetable } from '../player';
import { Elem } from '../weapons';
import { NO_GROUND } from '../world';
import type { Game } from '../game';
import { BossBase, BossBeat, BossDef, BossHit, BossKind, BossNet, Ray } from './types';

// ------------------------------------------------------------------ tuning
/** the GLB (Assets/bosses/angler/angler.glb) is drawn at this scale. The 2026-10 piranha is modelled
 *  at full size: a ~7 m oval hull carried high on pillar legs (belly ~4.4 m, hull top ~8.9 m, the
 *  dorsal fin to 11.5 m), so a 1.8 m cowboy stands about a fifth of it; it draws 1:1. */
export const ANGLER_SCALE = 1.0;
export const ANGLER_HP = 3000;
const LEASH = 30;
const STANDOFF = 8;
const WALK = 3.0, RUN = 6.0;
const TURN = 3.2;
export const GATES = [0.66, 0.33];
export const CRACK = 900;
const BELLY_OPEN = 1.6;
const WET_SECS = 5;
const PWET_SECS = 4;
const SHOCK_DMG = 45, SHOCK_R = 9, BACKFIRE = 14;
const FREEZE_SECS = 2;
const PIRANHA_CAP = 3, PIRANHA_LOCK = 10, PIRANHA_HP = 40;
const BARRIER_MAX = 6;
/** flood radius by phase (index 0 = intro puddle); 4 = the death surge */
export const FLOOD = [7, 7, 16, 28, 40];
export const FLOOD_DEPTH = 0.35;

/** attack ids (Ev.Boss Attack a = id: the TELL; net.atk) */
export const enum Atk { None = 0, Palm = 1, Kick = 2, Stomp = 3, Douse = 4, Hose = 5, Geyser = 6, Summon = 7, Bomb = 8, Barrier = 9, Maelstrom = 10 }
export const ATK_NAMES = ['', 'PALM SHOT', 'KICK', 'STOMP', 'SELF-DOUSE', 'PRESSURE HOSE', 'GEYSER FIELD', 'PIRANHA CALL', 'BOMB HURL', 'WATER BARRIER', 'MAELSTROM'];
//                    -   palm  kick  stomp douse hose  geys  summ  bomb  barr  mael
const TELL = [0, 0.7, 0.8, 0.9, 0.8, 0.8, 1.0, 1.0, 0.8, 0.8, 1.6];
const EXEC = [0, 0.6, 0.35, 0.25, 0.5, 2.4, 0.5, 0.4, 0.6, BARRIER_MAX, 0.9];
const REC = [0, 1.0, 1.6, 1.6, 0.9, 1.2, 1.2, 0.9, 1.2, 0.9, 2.4];
const CD = [0, 4, 3.5, 8, 9, 9, 11, 12, 10, 14, 30];
/** attack phase: 0 none, 1 tell, 2 execute, 3 recover, 4 the movement beat */
export const enum Ast { Idle = 0, Tell = 1, Exec = 2, Rec = 3, Beat = 4 }
/** net.phase bits */
export const enum AngBit { PhaseMask = 3, Gate = 4, Wet = 8, Belly = 16, Barrier = 32, Intro = 64, Frozen = 128 }
/** this boss's own Ev.Boss beats */
export const enum AngEv { Gate = 16, GateBreak = 17, Phase = 18, Douse = 19, Shock = 20, Freeze = 21, Hiss = 22, Panel = 23, Barrier = 24, BarrierBreak = 25, Geyser = 26, Slam = 27, Backfire = 28, Summon = 29 }

const PALM_DMG = 26;
const KICK_R = 6.2, KICK_COS = Math.cos((80 / 2) * Math.PI / 180), KICK_DMG = 36, KICK_PUSH = 22;
const STOMP_R = 7.5, STOMP_DMG = 30, STOMP_PUSH = 20;
const DOUSE_R = 7;
const HOSE_R = 13, HOSE_COS = Math.cos((34 / 2) * Math.PI / 180), HOSE_DMG = 8, HOSE_PUSH = 16;
const GEYSER_R = 2.4, GEYSER_DMG = 22, GEYSER_LIFT = 15;
const BOMB_FLIGHT = 1.0;
const MAEL_DMG = 55, MAEL_PUSH = 18, MAEL_RISE = 8;
const BUMP_DMG = 6, BUMP_GATE = secs(1);

// ---- part volumes, in model space (metres at scale 1, feet at 0, the Angler faces +Z), measured
// from the GLB's named nodes (node tools in sim/bosses/README.md §4). The client imports these.
type Box = readonly [number, number, number, number, number, number]; // x0 y0 z0 x1 y1 z1
export const EYE_L: Box = [1.45, 7.04, 2.18, 2.02, 7.67, 2.86];
export const EYE_R: Box = [-2.02, 7.04, 2.18, -1.45, 7.67, 2.86];
export const LURE: Box = [-0.45, 8.25, 4.15, 0.45, 9.0, 4.9];
export const BELLY: Box = [-0.45, 3.5, 1.05, 0.45, 4.4, 1.35];
/** the piranha hull (node `head`): a core box, the lower face + chin in front of it (kept below the
 *  eyes, which sit high on the flanks), the tail stalk + fin behind */
export const HEAD: Box = [-2.4, 4.5, -2.6, 2.4, 8.7, 1.8];
export const FACE: Box = [-1.9, 4.9, 1.8, 1.9, 6.9, 3.95];
export const TAIL: Box = [-1.0, 5.0, -6.0, 1.0, 8.0, -2.6];
export const JAW: Box = [-2.15, 4.66, 1.7, 2.15, 6.79, 4.95];
export const BODY: Box = [-1.45, 2.95, -1.4, 1.45, 4.6, 1.3];
export const ARM_L: Box = [1.93, 2.84, 0.0, 3.8, 5.9, 1.45];
export const ARM_R: Box = [-3.8, 2.84, 0.0, -1.93, 5.9, 1.45];
export const FORE_L: Box = [2.8, 0.0, 0.35, 4.35, 3.4, 2.35];
export const FORE_R: Box = [-4.35, 0.0, 0.35, -2.8, 3.4, 2.35];
export const LEGS: Box = [-2.3, 0, -1.2, 2.3, 3.81, 1.5];
/** the armour panels, in shed order (shoulders → thighs → back fin → shins), with their GLB node names */
export const PANEL_NODES = ['panel_shoulder_L', 'panel_shoulder_R', 'panel_thigh_L', 'panel_thigh_R', 'panel_back_fin', 'panel_shin_L', 'panel_shin_R'];
export const PANELS: Box[] = [
  [2.23, 4.67, -0.16, 3.58, 6.04, 1.66], [-3.58, 4.67, -0.16, -2.23, 6.04, 1.66],
  [1.06, 2.52, 0.3, 2.3, 3.56, 1.08], [-2.3, 2.52, 0.3, -1.06, 3.56, 1.08],
  [-0.25, 6.69, -3.3, 0.25, 11.51, -0.32],
  [1.01, 1.06, 0.57, 2.07, 2.26, 1.04], [-2.07, 1.06, 0.57, -1.01, 2.26, 1.04],
];
export const BARRIER_R = 6.5; // world metres, round the hull and the arms
export const BARRIER_Y = 5.4;

/** which part a shot found */
export const enum APart { None = 0, Chassis = 1, Eye = 2, Belly = 3, Lure = 4, Plating = 5, Barrier = 6 }
export const PART_ARMOR: { av: number; mul: number; weak: boolean }[] = [
  { av: 0, mul: 1, weak: false },
  { av: 4, mul: 0.25, weak: false }, // the AP5 chassis: everything clangs
  { av: 1, mul: 1.25, weak: true }, // headlamp eyes: always open
  { av: 0, mul: 1.5, weak: true }, // the drum, in a recovery window
  { av: 1, mul: 1, weak: true }, // the lure bulb (the gate's crack router)
  { av: 3, mul: 1, weak: false }, // bare plating under a shed panel
  { av: 4, mul: 0, weak: false }, // the water barrier: melee it
];
export const PART_NAMES = ['', 'CHASSIS · AP5', 'EMBER EYE · CRIT', 'DRUM · CRIT', 'LURE · CRIT', 'BARE PLATING · AV3', 'WATER BARRIER · MELEE IT'];

export interface AnglerState extends BossBase {
  kind: BossKind.Angler;
  homeX: number; homeZ: number;
  phase: number; gate: boolean; gatesDone: number; crack: number;
  atk: Atk; ast: Ast; atkT: number; atkDur: number;
  cd: Int32Array;
  target: number;
  aimX: number; aimZ: number;
  pts: { x: number; z: number }[];
  hitMask: number;
  wetT: number; pwet: Int16Array;
  bellyT: number; freezeT: number;
  barrier: boolean;
  panels: number; panelCd: number;
  flood: number;
  adds: number[]; addLock: number;
  lastHp: number; shotDealt: number; primedWas: number;
  bumpAt: Int32Array; backfireAt: Int32Array;
  introDone: boolean;
  deadT: number;
}

export interface AnglerNet extends BossNet {
  kind: BossKind.Angler;
  /** atk = the attack; phase = AngBit flags (low 2 bits: fight phase 1..3) */
  ast: Ast; prog: number;
  homeX: number; homeZ: number;
  /** the arena flood's radius now (m) */
  flood: number;
  crack: number;
  panels: number;
  adds: number;
  /** locked telegraph points (geyser columns, bomb landings, the hose / palm aim point) */
  pts: { x: number; z: number }[];
}

const groundAt = (g: Game, x: number, z: number, y: number) => {
  const t = g.world.terrain(x, z);
  return t === NO_GROUND ? y : t;
};

function targets(g: Game) {
  const out: PlayerState[] = [];
  for (const p of g.players.values()) if (isTargetable(p) && p.phase === 0) out.push(p);
  return out;
}

function body(g: Game, s: AnglerState) {
  for (const e of g.enemies) if (e.id === s.eid && !e.dead) return e;
  return null;
}

// ------------------------------------------------------------------ geometry (shared with the client)
export interface AngOpen { belly: boolean; barrier: boolean; panels: number }
/** Ray against the Angler at (x, y, z, yaw). Returns the nearest part (weak points win close calls). */
export function anglerRaycast(
  x: number, y: number, z: number, yaw: number, open: AngOpen,
  ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, maxT: number,
  out: { t: number; part: APart; idx: number },
) {
  out.t = -1; out.part = APart.None; out.idx = -1;
  const S = ANGLER_SCALE;
  // broad phase: a sphere round the whole fish
  {
    const cx = x - ox, cy = y + 5.4 * S - oy, cz = z - oz;
    const b = cx * dx + cy * dy + cz * dz;
    const c = cx * cx + cy * cy + cz * cz - (8.0 * S) * (8.0 * S);
    if (c > 0 && (b < 0 || b * b < c)) return out;
  }
  let best = maxT;
  if (open.barrier) {
    // the water barrier: a sphere round the chest stops every shot
    const cx = x - ox, cy = y + BARRIER_Y - oy, cz = z - oz;
    const b = cx * dx + cy * dy + cz * dz;
    const c = cx * cx + cy * cy + cz * cz - BARRIER_R * BARRIER_R;
    const disc = b * b - c;
    if (disc >= 0) {
      const t = c > 0 ? b - Math.sqrt(disc) : 0;
      if (t >= 0 && t < best) { out.t = t; out.part = APart.Barrier; return out; }
    }
  }
  // into the model frame (model +Z = forward; group yaw = yaw + π like every boss), unscaled
  const c = Math.cos(yaw + Math.PI), s = Math.sin(yaw + Math.PI);
  const px = (ox - x) / S, pz = (oz - z) / S;
  const lox = c * px - s * pz, loz = s * px + c * pz, loy = (oy - y) / S;
  const ldx = (c * dx - s * dz) / S, ldz = (s * dx + c * dz) / S, ldy = dy / S;
  const test = (bx: Box, part: APart, bias: number, idx = -1) => {
    const t = rayBox(lox, loy, loz, ldx, ldy, ldz, bx);
    if (t >= 0 && t - bias < best) { best = t - bias; out.t = t; out.part = part; out.idx = idx; }
  };
  test(EYE_L, APart.Eye, 0.12, 0); test(EYE_R, APart.Eye, 0.12, 1);
  test(LURE, APart.Lure, 0.12);
  if (open.belly) test(BELLY, APart.Belly, 0.15);
  // the armour panels are chassis until a blast knocks them off; then the plating under them is soft
  for (let i = 0; i < PANELS.length; i++) test(PANELS[i], open.panels & (1 << i) ? APart.Plating : APart.Chassis, 0.05, i);
  test(HEAD, APart.Chassis, 0); test(FACE, APart.Chassis, 0); test(TAIL, APart.Chassis, 0);
  test(JAW, APart.Chassis, 0); test(BODY, APart.Chassis, 0);
  test(ARM_L, APart.Chassis, 0); test(ARM_R, APart.Chassis, 0); test(FORE_L, APart.Chassis, 0); test(FORE_R, APart.Chassis, 0);
  test(LEGS, APart.Chassis, 0);
  return out;
}

function rayBox(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, b: Box) {
  let tmin = 0, tmax = 1e9;
  const ax = [ox, oy, oz], ad = [dx, dy, dz];
  for (let k = 0; k < 3; k++) {
    const lo = b[k], hi = b[k + 3];
    if (Math.abs(ad[k]) < 1e-9) { if (ax[k] < lo || ax[k] > hi) return -1; continue; }
    let t1 = (lo - ax[k]) / ad[k], t2 = (hi - ax[k]) / ad[k];
    if (t1 > t2) { const q = t1; t1 = t2; t2 = q; }
    if (t1 > tmin) tmin = t1;
    if (t2 < tmax) tmax = t2;
    if (tmin > tmax) return -1;
  }
  return tmin;
}

/** model-space point → world, for a body at (x, z, yaw) */
export function modelToWorld(x: number, z: number, yaw: number, lx: number, lz: number, out: { x: number; z: number }) {
  const a = yaw + Math.PI, c = Math.cos(a), s = Math.sin(a);
  out.x = x + (lx * c + lz * s) * ANGLER_SCALE; out.z = z + (-lx * s + lz * c) * ANGLER_SCALE;
  return out;
}

export const isOpen = (s: AnglerState): AngOpen => ({ belly: s.bellyT > 0, barrier: s.barrier, panels: s.panels });

// ------------------------------------------------------------------ the def
const hp = { x: 0, y: 0, z: 0, yaw: 0 };
const ph = { t: -1, part: APart.None, idx: -1 };
const o2 = { x: 0, z: 0 };

export const ANGLER: BossDef<AnglerState> = {
  kind: BossKind.Angler,
  name: 'THE ANGLER',
  recall: 'RECALL NOTICE: THE ANGLER. A WASHING MACHINE THAT THINKS IT IS A GUNSLINGER. DO NOT GET IT WET. IT IS WET.',
  types: [EType.Angler],
  procs: true,

  spawn(g, x, z, lookX, lookZ, n, tierMul) {
    const e = g.spawnEnemy(EType.Angler, x, z);
    e.maxHp = e.hp = ANGLER_HP * (1 + 0.35 * (n - 1)) * tierMul;
    e.y = groundAt(g, x, z, e.y);
    e.stDur = secs(2.6); // it lurks under the puddle, then erupts
    e.yaw = Math.atan2(-(lookX - x), -(lookZ - z));
    for (let i = 0; i < e.hx.length; i++) { e.hx[i] = e.x; e.hy[i] = e.y; e.hz[i] = e.z; e.hyaw[i] = e.yaw; }
    const cd = new Int32Array(16);
    for (let i = 1; i < cd.length; i++) cd[i] = secs(i === Atk.Palm ? 1.5 : 3 + (i % 4));
    return {
      kind: BossKind.Angler, eid: e.id, homeX: x, homeZ: z,
      phase: 1, gate: false, gatesDone: 0, crack: 0,
      atk: Atk.None, ast: Ast.Idle, atkT: 0, atkDur: 1, cd, target: -1, aimX: lookX, aimZ: lookZ, pts: [], hitMask: 0,
      wetT: 0, pwet: new Int16Array(32), bellyT: 0, freezeT: 0, barrier: false, panels: 0, panelCd: 0,
      flood: FLOOD[0], adds: [], addLock: 0, lastHp: e.hp, shotDealt: 0, primedWas: -1,
      bumpAt: new Int32Array(32).fill(-9999), backfireAt: new Int32Array(32).fill(-9999), introDone: false, deadT: 0,
    };
  },

  stepEnemy(g, s, e) {
    if (e.id === s.eid) stepAngler(g, s, e);
  },

  raycast(g, s, r: Ray, view, maxT, out: BossHit) {
    out.t = -1; out.e = null;
    const e = body(g, s);
    if (!e || e.st === EState.Spawn) return out;
    historyAt(e, view, g.tick, hp);
    anglerRaycast(hp.x, hp.y, hp.z, hp.yaw, isOpen(s), r.ox, r.oy, r.oz, r.dx, r.dy, r.dz, maxT, ph);
    if (ph.t < 0) return out;
    const pa = PART_ARMOR[ph.part];
    out.t = ph.t; out.e = e; out.av = pa.av; out.mul = pa.mul;
    // the gold points count as head shots (a Gunslinger's headshot detonates a prime through them)
    out.weak = pa.weak; out.head = pa.weak;
    out.part = ph.part; out.idx = ph.idx;
    return out;
  },

  tookHit(g, s, hit, dealt) {
    const e = hit.e;
    if (!e) return;
    s.shotDealt += dealt;
    // a detonation through a shot (the prime was spent): it knocks a panel off
    if (s.primedWas >= 0 && e.primedBy < 0 && dealt > 40) shedPanel(g, s, e);
    // the gate's crack meter: gold hits feed it (the lure twice over)
    if (e.hyper && s.gate && hit.weak) {
      s.crack += dealt * 10 * (hit.part === APart.Lure ? 2 : 1);
      if (s.crack >= CRACK) shatterGate(g, s, e);
    }
  },

  died(_g, s, e) {
    if (e.id !== s.eid) return false;
    s.deadT = 1;
    return true;
  },

  proc(g, s, e, elem, owner) {
    if (e.id !== s.eid || e.dead) return;
    const wet = s.wetT > 0 || s.phase >= 3;
    if (elem === Elem.Fire && wet) {
      // fire hisses straight off a wet fish (the in-fiction counter, never a resistance number)
      e.burnT = 0;
      g.emit(Ev.Boss, [e.id, AngEv.Hiss, 0, 0]);
      return;
    }
    if (elem === Elem.Shock && wet) electrocute(g, s, e, owner);
    if (elem === Elem.Ice && wet && s.freezeT <= 0) {
      s.freezeT = secs(FREEZE_SECS);
      e.vulnT = Math.max(e.vulnT, secs(FREEZE_SECS));
      if (s.ast === Ast.Tell || s.ast === Ast.Exec) endAttack(g, s, e, true);
      g.emit(Ev.Boss, [e.id, AngEv.Freeze, 0, 0]);
    }
  },

  note(_s, kind, a) {
    switch (kind) {
      case AngEv.Gate: return a === 1 ? 'HIGH TIDE COMING: IT SHELLS UP. PRIME IT, THEN DETONATE (OR CRACK IT THROUGH THE LURE).' : 'MAELSTROM COMING: IT SHELLS UP AGAIN. PRIME, DETONATE.';
      case AngEv.Phase: return a === 2 ? 'HIGH TIDE. IT IS FLOODING THE ARENA: WET FISH TAKE SHOCK. DRY COWBOYS DO NOT GET ZAPPED.' : 'MAELSTROM. THE ARENA IS UNDER WATER: FIRE IS DEAD, SHOCK IS KING. FIND A DRY PERCH.';
      case AngEv.Barrier: return 'WATER BARRIER: BULLETS WILL NOT GET THROUGH. SOMEBODY RUN IN AND HIT IT.';
      case AngEv.Panel: return 'A PANEL CAME OFF. THE PLATING UNDER IT IS SOFT (AV3).';
    }
    return undefined;
  },

  enc(w: Writer, g, s) {
    const e = body(g, s);
    w.u16w(s.eid); w.u8w(s.atk);
    let bits = s.phase & 3;
    if (s.gate) bits |= AngBit.Gate;
    if (s.wetT > 0 || s.phase >= 3) bits |= AngBit.Wet;
    if (s.bellyT > 0) bits |= AngBit.Belly;
    if (s.barrier) bits |= AngBit.Barrier;
    if (!s.introDone) bits |= AngBit.Intro;
    if (s.freezeT > 0) bits |= AngBit.Frozen;
    w.u8w(bits); w.u8w(s.ast);
    w.u8w(Math.round(clamp(1 - s.atkT / Math.max(1, s.atkDur), 0, 1) * 255));
    w.cmw(s.homeX); w.cmw(s.homeZ);
    w.u8w(Math.min(255, Math.round(s.flood * 4)));
    w.u8w(Math.round(clamp(s.crack / CRACK, 0, 1) * 255));
    w.u8w(s.panels);
    w.u8w(Math.min(15, s.adds.length));
    const n = s.ast === Ast.Tell || s.ast === Ast.Exec ? Math.min(4, s.pts.length) : 0;
    w.u8w(n);
    for (let i = 0; i < n; i++) { w.cmw(s.pts[i].x); w.cmw(s.pts[i].z); }
    void e;
  },

  dec(r: Reader): AnglerNet {
    const eid = r.u16(), atk = r.u8(), phase = r.u8(), ast = r.u8() as Ast, prog = r.u8() / 255;
    const homeX = r.cm(), homeZ = r.cm(), flood = r.u8() / 4, crack = r.u8() / 255, panels = r.u8(), adds = r.u8();
    const n = r.u8();
    const pts: { x: number; z: number }[] = [];
    for (let i = 0; i < n; i++) pts.push({ x: r.cm(), z: r.cm() });
    return { kind: BossKind.Angler, eid, atk, phase, ast, prog, homeX, homeZ, flood, crack, panels, adds, pts };
  },
};

// ------------------------------------------------------------------ helpers
function setSt(e: Enemy, st: EState, dur: number) {
  e.st = st; e.stT = 0; e.stDur = Math.max(1, dur);
}

function nearest(g: Game, s: AnglerState, e: Enemy) {
  let best: PlayerState | null = null, bd = 1e9;
  for (const p of targets(g)) {
    const d = len2(p.x - e.x, p.z - e.z) + (p.id === s.target ? -3 : 0);
    if (d < bd) { bd = d; best = p; }
  }
  return best;
}

function clampLeash(s: AnglerState, x: number, z: number, out: { x: number; z: number }, r = LEASH) {
  let ox = x - s.homeX, oz = z - s.homeZ;
  const l = len2(ox, oz);
  if (l > r) { ox *= r / l; oz *= r / l; }
  out.x = s.homeX + ox; out.z = s.homeZ + oz;
  return out;
}

/** a cowboy standing in the flood (on the ground, not up a perch) */
function inFlood(g: Game, s: AnglerState, p: PlayerState) {
  if (len2(p.x - s.homeX, p.z - s.homeZ) > s.flood) return false;
  return p.y - groundAt(g, p.x, p.z, p.y) < 0.9;
}

function wetPlayer(s: AnglerState, p: PlayerState, secsWet = PWET_SECS) {
  const i = p.id & 31;
  s.pwet[i] = Math.max(s.pwet[i], secs(secsWet));
}

/** knock a cowboy away from (fx, fz); near the rim the shove turns inboard (G4) */
function shove(g: Game, s: AnglerState, p: PlayerState, fx: number, fz: number, dmg: number, push: number) {
  let kx = p.x - fx, kz = p.z - fz;
  const l = len2(kx, kz) || 1;
  kx /= l; kz /= l;
  const hx = s.homeX - p.x, hz = s.homeZ - p.z;
  if (len2(hx, hz) > LEASH - 4 && kx * hx + kz * hz < 0) { kx = -kx; kz = -kz; }
  return g.hurtPlayer(p, dmg, fx, fz, 0, kx * push, kz * push);
}

function electrocute(g: Game, s: AnglerState, e: Enemy, owner: number) {
  // the big swing: the wet fish and every wet piranha near it (armour-bypassing)
  const hp0 = e.hp;
  g.damageEnemy(e, SHOCK_DMG, 4, owner, 8, e.x, e.y + 3, e.z, true);
  s.shotDealt += Math.max(0, hp0 - e.hp); // not a blast: no panel for it
  let n = 0;
  for (const o of g.enemies) {
    if (o.dead || o.type !== EType.Swarmer || s.adds.indexOf(o.id) < 0) continue;
    if (len2(o.x - e.x, o.z - e.z) > SHOCK_R) continue;
    g.damageEnemy(o, SHOCK_DMG, 4, owner, 8, o.x, o.y + 0.6, o.z, true);
    n++;
  }
  g.emit(Ev.Boss, [e.id, AngEv.Shock, n, 0]);
  // the risk half: a WET shooter eats a backfire through his own shield (once a second)
  const p = owner >= 0 ? g.players.get(owner) : undefined;
  if (p && s.pwet[p.id & 31] > 0 && g.tick - s.backfireAt[p.id & 31] >= secs(1)) {
    s.backfireAt[p.id & 31] = g.tick;
    if (g.hurtPlayer(p, BACKFIRE, p.x, p.z, 0)) g.emit(Ev.Boss, [e.id, AngEv.Backfire, p.id & 255, 0]);
  }
}

function shedPanel(g: Game, s: AnglerState, e: Enemy) {
  if (s.panelCd > 0) return;
  for (let i = 0; i < PANELS.length; i++) {
    if (s.panels & (1 << i)) continue;
    s.panels |= 1 << i;
    s.panelCd = secs(1);
    g.emit(Ev.Boss, [e.id, AngEv.Panel, i, 0]);
    if (i === 0) g.emit(Ev.Notice, [], ANGLER.note!(s, AngEv.Panel, 0, 0)!);
    return;
  }
}

function startGate(g: Game, s: AnglerState, e: Enemy) {
  s.gate = true; s.crack = 0;
  e.hyper = 1;
  e.hp = Math.max(e.hp, e.maxHp * GATES[s.gatesDone]); // the floor: no damage cascade skips a gate
  if (s.ast !== Ast.Idle) endAttack(g, s, e, true);
  g.emit(Ev.Boss, [e.id, AngEv.Gate, s.gatesDone + 1, 0]);
  g.emit(Ev.Notice, [], ANGLER.note!(s, AngEv.Gate, s.gatesDone + 1, 0)!);
}

function shatterGate(g: Game, s: AnglerState, e: Enemy) {
  if (!s.gate) return;
  s.gate = false; s.crack = 0; e.hyper = 0;
  s.gatesDone++;
  s.phase = Math.min(3, s.gatesDone + 1);
  e.stunT = Math.max(e.stunT, secs(3)); // the reward stagger (the belly hangs open through it)
  s.bellyT = secs(3);
  if (s.ast !== Ast.Idle) endAttack(g, s, e, true);
  g.emit(Ev.Boss, [e.id, AngEv.GateBreak, s.phase, 0]);
  g.emit(Ev.Boss, [e.id, AngEv.Phase, s.phase, 0]);
  g.emit(Ev.Notice, [], ANGLER.note!(s, AngEv.Phase, s.phase, 0)!);
  // the next phase's moves come in after a breath, not all at once
  for (let i = 1; i < s.cd.length; i++) s.cd[i] = Math.max(s.cd[i], secs(2 + (i % 3)));
  s.cd[Atk.Summon] = secs(3); s.cd[Atk.Douse] = secs(5); s.cd[Atk.Barrier] = secs(6); s.cd[Atk.Maelstrom] = secs(14);
}

function endAttack(g: Game, s: AnglerState, e: Enemy, cancel = false) {
  if (s.atk === Atk.Barrier) s.barrier = false;
  s.ast = Ast.Idle; s.atk = Atk.None; s.pts = [];
  setSt(e, EState.Chase, 0);
  void g; void cancel;
}

// ------------------------------------------------------------------ the brain
function stepAngler(g: Game, s: AnglerState, e: Enemy) {
  e.stT++;
  e.kx = e.kz = 0; // a five-ton fish does not get shoved
  // blasts that bit since the last tick (explosions, specials): a panel comes off
  const unexplained = s.lastHp - e.hp - s.shotDealt;
  if (unexplained >= 3 && s.introDone) shedPanel(g, s, e);
  s.shotDealt = 0;
  if (!stepStatus(e, g)) return;
  if (s.wetT > 0) s.wetT--;
  if ((s.wetT > 0 || s.phase >= 3) && e.burnT > 0) { e.burnT = 0; g.emit(Ev.Boss, [e.id, AngEv.Hiss, 0, 0]); }
  if (s.bellyT > 0) s.bellyT--;
  if (s.freezeT > 0) s.freezeT--;
  if (s.panelCd > 0) s.panelCd--;
  if (s.addLock > 0) s.addLock--;
  for (let i = 1; i < s.cd.length; i++) if (s.cd[i] > 0) s.cd[i]--;
  for (let i = 0; i < 32; i++) if (s.pwet[i] > 0) s.pwet[i]--;
  // priming flinches a robot for 0.75 s; the Angler only staggers for real (gate breaks, freezes)
  if (e.stunT > 0 && e.stunT < secs(1) && e.st !== EState.Stun) e.stunT = 0;

  // ---- the flood: its radius is the phase clock
  const goal = FLOOD[s.introDone ? s.phase : 0];
  s.flood += clamp(goal - s.flood, -3 * DT, 1.6 * DT);
  for (const p of targets(g)) if (inFlood(g, s, p)) wetPlayer(s, p, 1.5);

  // ---- piranhas: forget the dead (each kill buys a breather)
  if (s.adds.length) {
    const before = s.adds.length;
    s.adds = s.adds.filter((id) => g.enemies.some((o) => o.id === id && !o.dead));
    if (s.adds.length < before) s.addLock = secs(PIRANHA_LOCK);
  }

  const done = () => { s.lastHp = e.hp; s.primedWas = e.primedBy; };

  // ---- intro: it lurks under the puddle, then erupts
  if (e.st === EState.Spawn) {
    if (e.stT >= e.stDur) {
      setSt(e, EState.Chase, 0);
      s.introDone = true;
      g.emit(Ev.Boss, [e.id, BossBeat.Intro, 1, 0]);
    }
    return done();
  }

  // ---- the gates
  const frac = e.hp / e.maxHp;
  if (!s.gate && s.gatesDone < GATES.length && frac <= GATES[s.gatesDone]) startGate(g, s, e);
  if (s.gate && !e.hyper) shatterGate(g, s, e); // a detonation broke it (game.detonate cleared the flag)

  // ---- staggered / frozen: stand there and take it
  if (s.freezeT > 0 || e.stunT > 0) {
    if (e.stunT > 0) { e.stunT--; s.bellyT = Math.max(s.bellyT, 2); }
    setSt(e, EState.Stun, Math.max(e.stunT, s.freezeT));
    return done();
  }

  // ---- target
  const t = nearest(g, s, e);
  s.target = t ? t.id : -1;
  const enr = s.phase >= 3;
  const cdMul = enr ? 0.7 : 1, mvMul = enr ? 1.25 : 1;

  // ---- the gate: it hunkers in the flood, shell up, until the posse breaks it
  if (s.gate) {
    if (t) faceTo(e, t.x, t.z, TURN * 0.6);
    setSt(e, EState.Windup, secs(1));
    bump(g, s, e);
    return done();
  }

  // ---- the beat: TELL → EXECUTE → RECOVER → move
  if (s.ast !== Ast.Idle) {
    s.atkT--;
    if (s.ast === Ast.Exec) execute(g, s, e);
    if (s.atkT <= 0) advance(g, s, e, cdMul);
  }
  if (s.ast === Ast.Idle && t) {
    const pick = pickAttack(g, s, e, t);
    if (pick !== Atk.None) startAttack(g, s, e, pick, t, cdMul);
  }

  // ---- movement: only between attacks (the tell is readable because it stands still)
  if ((s.ast === Ast.Idle || s.ast === Ast.Beat) && t) {
    const d = len2(t.x - e.x, t.z - e.z);
    clampLeash(s, t.x, t.z, o2);
    const spd = (d > 16 ? RUN : WALK) * mvMul * (e.slowT > 0 ? 0.5 : 1);
    if (d > STANDOFF) walkTo(g, e, o2.x, o2.z, spd);
    else if (d < 3.5) walkTo(g, e, e.x - (t.x - e.x), e.z - (t.z - e.z), WALK * 0.6);
    faceTo(e, t.x, t.z, TURN);
    if (s.ast === Ast.Idle) setSt(e, EState.Chase, 0);
  }
  if (s.ast === Ast.Tell && t && (s.atk === Atk.Kick || s.atk === Atk.Palm)) faceTo(e, s.aimX, s.aimZ, TURN * 1.5);
  const gy = groundAt(g, e.x, e.z, e.y);
  if (s.atk !== Atk.Maelstrom || s.ast === Ast.Rec) e.y = e.y > gy ? Math.max(gy, e.y - 14 * DT) : gy;
  bump(g, s, e);
  done();
}

function pickAttack(g: Game, s: AnglerState, e: Enemy, t: PlayerState): Atk {
  const d = len2(t.x - e.x, t.z - e.z);
  const ph = s.phase;
  const ok = (a: Atk) => s.cd[a] <= 0;
  let near = 0;
  for (const p of targets(g)) if (len2(p.x - e.x, p.z - e.z) < STOMP_R) near++;
  if (ph >= 3 && ok(Atk.Maelstrom)) return Atk.Maelstrom;
  if (ph >= 3 && ok(Atk.Barrier)) return Atk.Barrier;
  if (ok(Atk.Stomp) && (near >= 2 || (d < 6 && !ok(Atk.Kick)))) return Atk.Stomp;
  if (ok(Atk.Kick) && d < 5.6) return Atk.Kick;
  if (ph >= 2) {
    if (ok(Atk.Summon) && s.addLock <= 0 && s.adds.length < PIRANHA_CAP) return Atk.Summon;
    if (ph === 2 && ok(Atk.Douse) && s.wetT <= 0) return Atk.Douse;
    if (ok(Atk.Geyser)) return Atk.Geyser;
    if (ok(Atk.Hose) && d < HOSE_R - 1) return Atk.Hose;
    if (ok(Atk.Bomb) && d > 10) return Atk.Bomb;
  }
  if (ok(Atk.Palm) && d > 6 && d < 45) return Atk.Palm;
  return Atk.None;
}

function startAttack(g: Game, s: AnglerState, e: Enemy, a: Atk, t: PlayerState, cdMul: number) {
  s.atk = a; s.ast = Ast.Tell;
  s.atkT = s.atkDur = secs(TELL[a]);
  s.cd[a] = secs(CD[a] * cdMul * g.rng.range(0.9, 1.15));
  s.hitMask = 0;
  // the impact point is locked when the TELL starts (G2): a telegraph never chases a dodge
  clampLeash(s, t.x, t.z, o2, LEASH + 6);
  s.aimX = o2.x; s.aimZ = o2.z;
  s.pts = [];
  if (a === Atk.Geyser) {
    for (const p of targets(g)) if (s.pts.length < 4 && len2(p.x - s.homeX, p.z - s.homeZ) < LEASH + 4) s.pts.push(clampLeash(s, p.x, p.z, { x: 0, z: 0 }));
    // fewer cowboys than columns: the rest bracket the target
    while (s.pts.length < 4) {
      const k = s.pts.length, ang = g.rng.range(0, Math.PI * 2), r = g.rng.range(4, 7);
      const x = s.aimX + Math.cos(ang + k * 2.1) * r, z = s.aimZ + Math.sin(ang + k * 2.1) * r;
      s.pts.push(g.world.terrain(x, z) === NO_GROUND ? { x: s.aimX, z: s.aimZ } : clampLeash(s, x, z, { x: 0, z: 0 }));
    }
  } else if (a === Atk.Bomb) {
    for (let k = 0; k < 3; k++) {
      const ang = g.rng.range(0, Math.PI * 2), r = k === 0 ? 0 : g.rng.range(3, 6);
      const x = s.aimX + Math.cos(ang) * r, z = s.aimZ + Math.sin(ang) * r;
      s.pts.push(g.world.terrain(x, z) === NO_GROUND ? { x: s.aimX, z: s.aimZ } : { x, z });
    }
  } else if (a === Atk.Palm || a === Atk.Hose || a === Atk.Kick) {
    s.pts.push({ x: s.aimX, z: s.aimZ });
  }
  setSt(e, EState.Windup, s.atkDur);
  g.emit(Ev.Boss, [e.id, BossBeat.Attack, a, 0]);
}

function advance(g: Game, s: AnglerState, e: Enemy, cdMul: number) {
  const a = s.atk;
  switch (s.ast) {
    case Ast.Tell:
      s.ast = Ast.Exec; s.atkT = s.atkDur = secs(EXEC[a]);
      setSt(e, EState.Attack, s.atkDur);
      begin(g, s, e);
      break;
    case Ast.Exec:
      finish(g, s, e);
      s.ast = Ast.Rec; s.atkT = s.atkDur = secs(REC[a]);
      // the punish read: a kick / stomp leaves the drum hanging open
      if (a === Atk.Kick || a === Atk.Stomp || a === Atk.Maelstrom) s.bellyT = secs(BELLY_OPEN + (a === Atk.Maelstrom ? 1.2 : 0));
      setSt(e, EState.Recover, s.atkDur);
      break;
    case Ast.Rec:
      s.ast = Ast.Beat; s.atkT = s.atkDur = secs(g.rng.range(0.6, 1.4) * (cdMul < 1 ? 0.8 : 1));
      s.pts = [];
      setSt(e, EState.Chase, 0);
      break;
    default:
      endAttack(g, s, e);
  }
}

/** the first frame of an attack's execute */
function begin(g: Game, s: AnglerState, e: Enemy) {
  switch (s.atk) {
    case Atk.Douse: {
      s.wetT = secs(WET_SECS);
      e.burnT = 0;
      for (const p of targets(g)) if (len2(p.x - e.x, p.z - e.z) < DOUSE_R) wetPlayer(s, p);
      g.emit(Ev.Boss, [e.id, AngEv.Douse, 0, 0]);
      break;
    }
    case Atk.Summon: {
      const n = PIRANHA_CAP - s.adds.length;
      for (let i = 0; i < n; i++) {
        const a = g.rng.range(0, Math.PI * 2), r = Math.max(6, s.flood * g.rng.range(0.6, 0.95));
        const x = s.homeX + Math.cos(a) * r, z = s.homeZ + Math.sin(a) * r;
        if (g.world.terrain(x, z) === NO_GROUND) continue;
        g.emit(Ev.SpawnWarn, [x, g.world.support(x, z, 60), z, EType.Swarmer, secs(0.8)]);
        const f = g.spawnEnemy(EType.Swarmer, x, z);
        f.maxHp = f.hp = PIRANHA_HP; // glass-jawed: one electrocution clears the school
        s.adds.push(f.id);
      }
      g.emit(Ev.Boss, [e.id, AngEv.Summon, n, 0]);
      break;
    }
    case Atk.Barrier:
      s.barrier = true;
      g.emit(Ev.Boss, [e.id, AngEv.Barrier, 0, 0]);
      g.emit(Ev.Notice, [], ANGLER.note!(s, AngEv.Barrier, 0, 0)!);
      break;
    case Atk.Bomb: {
      // three lobs from the left palm onto the locked rings
      modelToWorld(e.x, e.z, e.yaw, 3.6, 0.9, o2); // the left hand, raised high
      const oy = e.y + 7.4 * ANGLER_SCALE;
      const gr = BULLETS[BKind.Bomb].gravity;
      for (let k = 0; k < s.pts.length; k++) {
        const p = s.pts[k];
        const ty = groundAt(g, p.x, p.z, e.y);
        const v = lobVelocity(o2.x, oy, o2.z, p.x, ty, p.z, BOMB_FLIGHT + k * 0.18, gr);
        g.fireBullet(BKind.Bomb, o2.x, oy, o2.z, v.vx, v.vy, v.vz, e.id);
      }
      break;
    }
    case Atk.Palm: palmShot(g, s, e, -0.05); break;
  }
}

/** every tick of an attack's execute */
function execute(g: Game, s: AnglerState, e: Enemy) {
  const k = 1 - s.atkT / Math.max(1, s.atkDur);
  switch (s.atk) {
    case Atk.Palm:
      if (s.atkT === Math.floor(s.atkDur * 0.5)) palmShot(g, s, e, 0.05);
      break;
    case Atk.Hose: {
      // a sustained jet down the locked line: the first touch hurts and soaks, the rest just shoves
      const fx = s.aimX - e.x, fz = s.aimZ - e.z, fl = len2(fx, fz) || 1;
      for (const p of targets(g)) {
        const dx = p.x - e.x, dz = p.z - e.z, d = len2(dx, dz);
        if (d > HOSE_R || d < 0.5 || Math.abs(p.y - e.y) > 6) continue;
        if ((dx * fx + dz * fz) / (d * fl) < HOSE_COS) continue;
        const bit = 1 << (p.id & 31);
        wetPlayer(s, p);
        if (!(s.hitMask & bit)) { s.hitMask |= bit; shove(g, s, p, e.x, e.z, HOSE_DMG, HOSE_PUSH * 0.5); }
        else { p.vx += (fx / fl) * HOSE_PUSH * DT; p.vz += (fz / fl) * HOSE_PUSH * DT; }
      }
      break;
    }
    case Atk.Maelstrom: {
      // it leaps out of the water, hangs, then crashes back in
      const gy = groundAt(g, e.x, e.z, e.y);
      e.y = gy + Math.sin(Math.min(1, k * 1.1) * Math.PI) * MAEL_RISE;
      break;
    }
    case Atk.Barrier: {
      // the lock breaks to melee: someone ran in and hit it
      for (const v of g.events) {
        if (v.t !== Ev.Melee || v.f[1] === 0) continue;
        const p = g.players.get(v.f[0]);
        if (!p || len2(p.x - e.x, p.z - e.z) > ENEMIES[EType.Angler].radius + 5.0) continue;
        s.barrier = false;
        e.stunT = secs(2);
        s.bellyT = secs(2);
        g.emit(Ev.Boss, [e.id, AngEv.BarrierBreak, p.id & 255, 0]);
        endAttack(g, s, e);
        return;
      }
      break;
    }
  }
}

/** the last frame of an attack's execute: the impact */
function finish(g: Game, s: AnglerState, e: Enemy) {
  const fx = -Math.sin(e.yaw), fz = -Math.cos(e.yaw);
  switch (s.atk) {
    case Atk.Kick:
      for (const p of targets(g)) {
        const dx = p.x - e.x, dz = p.z - e.z, d = len2(dx, dz);
        if (d > KICK_R || Math.abs(p.y - e.y) > 3) continue;
        if (d > 1 && (dx * fx + dz * fz) / d < KICK_COS) continue;
        shove(g, s, p, e.x, e.z, KICK_DMG, KICK_PUSH);
      }
      break;
    case Atk.Stomp:
      for (const p of targets(g)) {
        const d = len2(p.x - e.x, p.z - e.z);
        if (d > STOMP_R || p.y - groundAt(g, p.x, p.z, p.y) > 1.1) continue; // jump it
        shove(g, s, p, e.x, e.z, STOMP_DMG, STOMP_PUSH);
      }
      g.emit(Ev.Boss, [e.id, AngEv.Slam, 0, 0]);
      break;
    case Atk.Geyser:
      for (const c of s.pts) {
        for (const p of targets(g)) {
          const bit = 1 << (p.id & 31);
          if (s.hitMask & bit) continue;
          if (len2(p.x - c.x, p.z - c.z) > GEYSER_R || p.y - groundAt(g, c.x, c.z, p.y) > 3) continue;
          s.hitMask |= bit;
          if (g.hurtPlayer(p, GEYSER_DMG, c.x, c.z, 0)) p.vy = Math.max(p.vy, GEYSER_LIFT);
          wetPlayer(s, p);
        }
      }
      g.emit(Ev.Boss, [e.id, AngEv.Geyser, s.pts.length, 0]);
      break;
    case Atk.Maelstrom:
      e.y = groundAt(g, e.x, e.z, e.y);
      // the whole flood slams: anyone standing in the water is hit once and swept back
      for (const p of targets(g)) {
        if (!inFlood(g, s, p) && len2(p.x - e.x, p.z - e.z) > 6) continue;
        if (p.y - groundAt(g, p.x, p.z, p.y) > 1.2) continue;
        shove(g, s, p, e.x, e.z, MAEL_DMG, MAEL_PUSH);
        wetPlayer(s, p);
      }
      g.emit(Ev.Boss, [e.id, AngEv.Slam, 1, 0]);
      break;
    case Atk.Barrier:
      s.barrier = false; // nobody broke it: it drops on its own (never permanent)
      break;
  }
}

function palmShot(g: Game, s: AnglerState, e: Enemy, spread: number) {
  // an Iron-Man bolt from the right palm at the locked aim point
  modelToWorld(e.x, e.z, e.yaw, -3.5, 3.3, o2); // the right palm, levelled at the aim
  const ox = o2.x, oy = e.y + 4.8 * ANGLER_SCALE, oz = o2.z;
  const ty = groundAt(g, s.aimX, s.aimZ, e.y) + 1.1;
  let ax = s.aimX - ox, ay = ty - oy, az = s.aimZ - oz;
  const l = Math.sqrt(ax * ax + ay * ay + az * az) || 1;
  ax /= l; ay /= l; az /= l;
  const c = Math.cos(spread), sn = Math.sin(spread);
  const rx = ax * c - az * sn, rz = ax * sn + az * c;
  const sp = BULLETS[BKind.Slug].speed * 0.55;
  g.fireBullet(BKind.Slug, ox, oy, oz, rx * sp, ay * sp, rz * sp, e.id);
  void PALM_DMG;
}

function walkTo(g: Game, e: Enemy, x: number, z: number, speed: number) {
  const dx = x - e.x, dz = z - e.z, d = len2(dx, dz);
  const step = speed * DT;
  let nx = x, nz = z;
  if (d > step) { nx = e.x + (dx / d) * step; nz = e.z + (dz / d) * step; }
  if (g.world.terrain(nx, nz) === NO_GROUND) return;
  e.vx = (nx - e.x) / DT; e.vz = (nz - e.z) / DT;
  e.x = nx; e.z = nz;
}

function faceTo(e: Enemy, x: number, z: number, rate: number) {
  const want = Math.atan2(-(x - e.x), -(z - e.z));
  const d = wrapAngle(want - e.yaw);
  e.yaw = wrapAngle(e.yaw + clamp(d, -rate * DT, rate * DT));
}

/** walking into a five-ton fish: knockback first, damage ≤ 10 (G4) */
function bump(g: Game, s: AnglerState, e: Enemy) {
  const r = ENEMIES[EType.Angler].radius + 0.4;
  for (const p of targets(g)) {
    const i = p.id & 31;
    if (g.tick - s.bumpAt[i] < BUMP_GATE) continue;
    if (len2(p.x - e.x, p.z - e.z) > r || p.y > e.y + 5 || p.y < e.y - 1) continue;
    s.bumpAt[i] = g.tick;
    shove(g, s, p, e.x, e.z, BUMP_DMG, 12);
  }
}

